/**
 * ユーザーコスト上限セクション
 *
 * ユーザー詳細画面に表示し、実効上限・使用量・使用率の表示と
 * 個別上限の設定・削除を行う。
 */
import { DollarSign, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import {
  useDeleteUserCostLimit,
  useSetUserCostLimit,
  useUserCostLimitDetail,
} from '../../hooks/use-admin-api';
import { formatCurrency } from './chart-utils';

interface UserCostLimitSectionProps {
  email: string;
}

function getUsageColor(usagePercent: number | null): string {
  if (usagePercent === null) return 'text-muted-foreground';
  if (usagePercent >= 100) return 'text-red-400';
  if (usagePercent >= 80) return 'text-yellow-400';
  return 'text-foreground';
}

export function UserCostLimitSection({ email }: UserCostLimitSectionProps) {
  const { data: detail, isLoading } = useUserCostLimitDetail(email);
  const setUserLimit = useSetUserCostLimit();
  const deleteUserLimit = useDeleteUserCostLimit();

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const handleEdit = useCallback(() => {
    setEditValue(detail?.userLimit ? detail.userLimit.limitUSD.toString() : '');
    setIsEditing(true);
  }, [detail]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditValue('');
  }, []);

  const handleSave = useCallback(async () => {
    const value = Number.parseFloat(editValue);
    if (Number.isNaN(value) || value < 0) return;
    await setUserLimit.mutateAsync({ email, limitUSD: value });
    setIsEditing(false);
  }, [editValue, email, setUserLimit]);

  const handleDelete = useCallback(async () => {
    await deleteUserLimit.mutateAsync(email);
  }, [email, deleteUserLimit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSave();
      if (e.key === 'Escape') handleCancel();
    },
    [handleSave, handleCancel],
  );

  const isMutating = setUserLimit.isPending || deleteUserLimit.isPending;

  if (isLoading) {
    return (
      <div className="bg-card rounded-xl p-6 border border-border mb-8">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!detail) return null;

  const usageColor = getUsageColor(detail.usagePercent);
  const isUserLimitSet = detail.userLimit !== null;
  const limitSource = isUserLimitSet ? '個別設定' : detail.defaultLimit ? 'デフォルト' : '未設定';

  return (
    <div className="bg-card rounded-xl p-6 border border-border mb-8">
      <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-4">
        <DollarSign className="w-5 h-5" />
        コスト上限 ({detail.period.year}/{String(detail.period.month).padStart(2, '0')})
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 左: ステータス表示 */}
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">実効上限</span>
            <span className="text-foreground">
              {detail.effectiveLimit !== null ? formatCurrency(detail.effectiveLimit) : '制限なし'}
              <span className="ml-1 text-xs text-muted-foreground">({limitSource})</span>
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">当月使用量</span>
            <span className={usageColor}>{formatCurrency(detail.currentCost)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">使用率</span>
            <span className={usageColor}>
              {detail.usagePercent !== null ? `${detail.usagePercent}%` : '-'}
            </span>
          </div>

          {/* プログレスバー */}
          {detail.usagePercent !== null && (
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  detail.usagePercent >= 100
                    ? 'bg-red-500'
                    : detail.usagePercent >= 80
                      ? 'bg-yellow-500'
                      : 'bg-primary-500'
                }`}
                style={{ width: `${Math.min(detail.usagePercent, 100)}%` }}
              />
            </div>
          )}
        </div>

        {/* 右: 個別上限設定フォーム */}
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">個別上限</div>
          {isEditing ? (
            <div className="flex items-center gap-2">
              <span className="text-foreground">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-32 px-2 py-1 bg-muted border border-border rounded text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                // biome-ignore lint/a11y/noAutofocus: inline edit needs auto-focus for UX
                autoFocus
                disabled={isMutating}
              />
              <button
                type="button"
                onClick={handleSave}
                disabled={isMutating}
                className="px-3 py-1 text-xs bg-primary-500 text-white rounded hover:bg-primary-600 transition-colors disabled:opacity-50"
              >
                {isMutating ? <Loader2 className="w-3 h-3 animate-spin" /> : '保存'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isMutating}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-foreground">
                {isUserLimitSet ? formatCurrency(detail.userLimit?.limitUSD ?? 0) : '未設定'}
              </span>
              <button
                type="button"
                onClick={handleEdit}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                title="個別上限を設定"
              >
                <Pencil className="w-4 h-4" />
              </button>
              {isUserLimitSet && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isMutating}
                  className="p-1 text-muted-foreground hover:text-red-400 transition-colors disabled:opacity-50"
                  title="個別上限を削除（デフォルト上限に戻す）"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
          {!isUserLimitSet && detail.defaultLimit && (
            <p className="text-xs text-muted-foreground">
              デフォルト上限 ({formatCurrency(detail.defaultLimit.limitUSD)}) が適用されています
            </p>
          )}
          {isUserLimitSet && detail.userLimit && (
            <p className="text-xs text-muted-foreground">
              最終更新: {new Date(detail.userLimit.updatedAt).toLocaleString('ja-JP')} (
              {detail.userLimit.updatedBy})
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
